import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TableContainer,
  TableFooter,
  TablePagination,
} from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { LoadingIcon } from "components/Common/LoadingIcon"
import { SearchBar } from "components/Common/SearchBar"
import { useAppDispatch, useAppSelector } from "hooks"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import organizationService from "services/organizationService"
import { resetDevice } from "slices/deviceSlice"
import { resetLicensePool } from "slices/licensePoolSlice"
import { resetLicense } from "slices/licenseSlice"
import { setCurrentOrganization, setOrganizations } from "slices/organizationSlice"
import { resetTrainingReport } from "slices/trainingReportSlice"
import { resetUserGroup } from "slices/userGroupSlice"
import { resetUsers } from "slices/usersSlice"
import type { IOrganization } from "types/organizationTypes"
import { OrganizationListItemMenu } from "./OrganizationListItemMenu"

export function OrganizationList() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchCategory, setSearchCategory] = useState<string>("Name")
  const [searchText, setSeachText] = useState<string>("")
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [page, setPage] = useState(0)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const organizations = useAppSelector((state) => state.organization.organizations)

  useEffect(() => {
    if (organizations.length === 0) {
      setIsLoading(true)
    }

    organizationService
      .getOrganizations()
      .then((organizations) => {
        dispatch(setOrganizations(organizations))
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [dispatch, organizations.length])

  const handleClickOrganization = (organization: IOrganization) => {
    dispatch(resetDevice(undefined))
    dispatch(resetLicense(undefined))
    dispatch(resetLicensePool(undefined))
    dispatch(resetUserGroup(undefined))
    dispatch(resetUsers(undefined))
    dispatch(resetTrainingReport(undefined))
    dispatch(setCurrentOrganization(organization))
    navigate(organization.id)
  }

  const filterOrganizations = (
    filter: string,
    organizations: Array<IOrganization>,
  ): Array<IOrganization> => {
    return organizations.filter((organization) => {
      if (searchCategory === "Name") {
        return organization.name.toLowerCase().includes(filter.toLowerCase())
      }
      if (searchCategory === "Id") {
        return organization.id.toLowerCase().includes(filter.toLowerCase())
      }
      return false
    })
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <>
      {isLoading ? (
        <LoadingIcon />
      ) : (
        <TableContainer>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel id="search-group">Search by</InputLabel>
                <Select
                  fullWidth
                  value={searchCategory}
                  labelId="search-group"
                  label="Search by"
                  onChange={(e) => {
                    setSearchCategory(e.target.value)
                    setPage(0)
                  }}
                >
                  <MenuItem value={"Name"}>Name</MenuItem>
                  <MenuItem value={"Id"}>Id</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={9}>
              <SearchBar
                onChange={(value: string) => {
                  setSeachText(value)
                  setPage(0)
                }}
              />
            </Grid>
          </Grid>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right" padding="checkbox"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filterOrganizations(searchText, organizations)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((organization) => (
                  <TableRow
                    key={organization.id}
                    hover
                    onClick={() => handleClickOrganization(organization)}
                  >
                    <TableCell>{organization.name}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <OrganizationListItemMenu organization={organization} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filterOrganizations(searchText, organizations).length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
